import { create } from 'zustand';

import type {
  BasicParams,
  ProductionRow,
  DeliverabilityTestPoint,
  DeliverabilityTestType,
  ProductionForecastProjectState,
} from '../pages/productionForecast/types';

const STORAGE_KEY = 'production_forecast_projects';

const EMPTY_PARAMS: BasicParams = {
  reservoirPressure: '',
  reservoirTemperature: '',
  depth: '',
  effectiveThickness: '',
  porosity: '',
  permeability: '',
  waterSaturation: '',

  wellType: '水平井',
  totalDepth: '',
  horizontalLength: '',
  tubingInnerDiameter: '',

  stageCount: '',
  clusterCount: '',
  stageSpacing: '',
  clusterSpacing: '',
  fluidVolume: '',
  proppantMass: '',
  averageRate: '',
  fractureHalfLength: '',
  fractureHeight: '',
  fractureConductivity: '',
  srv: '',
};

function createEmptyProductionRow(
  id: number,
): ProductionRow {
  return {
    id,
    date: '',
    productionHours: '',
    gasRate: '',
    waterRate: '',
    tubingPressure: '',
    casingPressure: '',
    bottomHolePressure: '',
    cumulativeGas: '',
  };
}

function createEmptyDeliverabilityPoint(
  id: number,
): DeliverabilityTestPoint {
  return {
    id,
    reservoirPressure: '',
    bottomHolePressure: '',
    gasRate: '',
  };
}

function createEmptyProject():
  ProductionForecastProjectState {
  return {
    params: {
      ...EMPTY_PARAMS,
    },

    productionRows: [
      createEmptyProductionRow(1),
    ],

    deliverability: {
      testType: '稳定试井',

      points: [
        createEmptyDeliverabilityPoint(1),
        createEmptyDeliverabilityPoint(2),
        createEmptyDeliverabilityPoint(3),
        createEmptyDeliverabilityPoint(4),
      ],
    },
  };
}

type ProjectMap = Record<
  string,
  ProductionForecastProjectState
>;

function loadProjects(): ProjectMap {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return {};
    }

    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function saveProjects(
  projects: ProjectMap,
) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(projects),
    );
  } catch {
    // localStorage不可用时，
    // Store仍然可以在当前页面会话中工作。
  }
}

type ProductionRowField =
  Exclude<keyof ProductionRow, 'id'>;

type DeliverabilityField =
  Exclude<
    keyof DeliverabilityTestPoint,
    'id'
  >;

interface ProductionForecastStore {
  projects: ProjectMap;

  ensureProject: (
    projectId: number,
  ) => void;

  updateParam: (
    projectId: number,
    key: keyof BasicParams,
    value: string,
  ) => void;

  addProductionRow: (
    projectId: number,
  ) => void;

  updateProductionRow: (
    projectId: number,
    id: number,
    key: ProductionRowField,
    value: string,
  ) => void;

  deleteProductionRow: (
    projectId: number,
    id: number,
  ) => void;

  resetDataPreparation: (
    projectId: number,
  ) => void;

  setDeliverabilityTestType: (
    projectId: number,
    value: DeliverabilityTestType,
  ) => void;

  addDeliverabilityPoint: (
    projectId: number,
  ) => void;

  updateDeliverabilityPoint: (
    projectId: number,
    id: number,
    key: DeliverabilityField,
    value: string,
  ) => void;

  deleteDeliverabilityPoint: (
    projectId: number,
    id: number,
  ) => void;

  resetDeliverability: (
    projectId: number,
  ) => void;

  resetProject: (
    projectId: number,
  ) => void;
}

export const useProductionForecastStore =
  create<ProductionForecastStore>(
    (set) => {
      const commit = (
        updater: (
          projects: ProjectMap,
        ) => ProjectMap,
      ) => {
        set((state) => {
          const projects =
            updater(state.projects);

          saveProjects(projects);

          return {
            projects,
          };
        });
      };

      const updateProject = (
        projects: ProjectMap,
        projectId: number,
        updater: (
          current:
            ProductionForecastProjectState,
        ) => ProductionForecastProjectState,
      ): ProjectMap => {
        const key =
          String(projectId);

        const current =
          projects[key] ??
          createEmptyProject();

        return {
          ...projects,

          [key]:
            updater(current),
        };
      };

      return {
        projects: loadProjects(),

        ensureProject: (
          projectId,
        ) => {
          commit((projects) => {
            const key =
              String(projectId);

            if (projects[key]) {
              return projects;
            }

            return {
              ...projects,

              [key]:
                createEmptyProject(),
            };
          });
        },

        updateParam: (
          projectId,
          key,
          value,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                params: {
                  ...current.params,
                  [key]: value,
                },
              }),
            ),
          );
        },

        addProductionRow: (
          projectId,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                productionRows: [
                  ...current.productionRows,

                  createEmptyProductionRow(
                    Date.now(),
                  ),
                ],
              }),
            ),
          );
        },

        updateProductionRow: (
          projectId,
          id,
          key,
          value,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                productionRows:
                  current.productionRows.map(
                    (row) =>
                      row.id === id
                        ? {
                            ...row,
                            [key]:
                              value,
                          }
                        : row,
                  ),
              }),
            ),
          );
        },

        deleteProductionRow: (
          projectId,
          id,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => {
                if (
                  current
                    .productionRows
                    .length <= 1
                ) {
                  return current;
                }

                return {
                  ...current,

                  productionRows:
                    current
                      .productionRows
                      .filter(
                        (row) =>
                          row.id !==
                          id,
                      ),
                };
              },
            ),
          );
        },

        resetDataPreparation: (
          projectId,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                params: {
                  ...EMPTY_PARAMS,
                },

                productionRows: [
                  createEmptyProductionRow(
                    1,
                  ),
                ],
              }),
            ),
          );
        },

        setDeliverabilityTestType: (
          projectId,
          value,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                deliverability: {
                  ...current
                    .deliverability,

                  testType:
                    value,
                },
              }),
            ),
          );
        },

        addDeliverabilityPoint: (
          projectId,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                deliverability: {
                  ...current
                    .deliverability,

                  points: [
                    ...current
                      .deliverability
                      .points,

                    createEmptyDeliverabilityPoint(
                      Date.now(),
                    ),
                  ],
                },
              }),
            ),
          );
        },

        updateDeliverabilityPoint: (
          projectId,
          id,
          key,
          value,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                deliverability: {
                  ...current
                    .deliverability,

                  points:
                    current
                      .deliverability
                      .points
                      .map(
                        (point) =>
                          point.id ===
                          id
                            ? {
                                ...point,

                                [key]:
                                  value,
                              }
                            : point,
                      ),
                },
              }),
            ),
          );
        },

        deleteDeliverabilityPoint: (
          projectId,
          id,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => {
                if (
                  current
                    .deliverability
                    .points.length <= 3
                ) {
                  return current;
                }

                return {
                  ...current,

                  deliverability: {
                    ...current
                      .deliverability,

                    points:
                      current
                        .deliverability
                        .points
                        .filter(
                          (point) =>
                            point.id !==
                            id,
                        ),
                  },
                };
              },
            ),
          );
        },

        resetDeliverability: (
          projectId,
        ) => {
          commit((projects) =>
            updateProject(
              projects,
              projectId,
              (current) => ({
                ...current,

                deliverability: {
                  testType:
                    '稳定试井',

                  points: [
                    createEmptyDeliverabilityPoint(
                      1,
                    ),

                    createEmptyDeliverabilityPoint(
                      2,
                    ),

                    createEmptyDeliverabilityPoint(
                      3,
                    ),

                    createEmptyDeliverabilityPoint(
                      4,
                    ),
                  ],
                },
              }),
            ),
          );
        },

        resetProject: (
          projectId,
        ) => {
          commit((projects) => ({
            ...projects,

            [String(projectId)]:
              createEmptyProject(),
          }));
        },
      };
    },
  );